import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  AccountBalance as BankIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import AlertModal from "../../components/AlertModal";
import ConfirmModal from "../../components/ConfirmModal";
import { hasWritePermission } from "../../utils/menuFilter";

const Bank = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [bankData, setBankData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});

  const [openModal, setOpenModal] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [newBank, setNewBank] = useState({
    contactNumber: "",
    status: "Active",
  });

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedBankForDelete, setSelectedBankForDelete] = useState(null);

  const fetchBanks = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${baseURL}/api/globalMasterDetails?type=bank`);
      if (!response.ok) {
        throw new Error(`Failed to fetch Banks: ${response.statusText}`);
      }
      const data = await response.json();
      const banks = Array.isArray(data.data) 
        ? data.data.map((item) => ({
            id: item._id,
            name: item.details?.bankName || 'Unknown',
            contactPersonName: item.details?.contactPersonName || '',
            contactNumber: item.details?.contactNumber || '',
            status: item.status === true ? 'Active' : 'Inactive',
            rawStatus: item.status,
          }))
        : [];
      setBankData(banks);
    } catch (err) {
      setError(err.message);
      setBankData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  const getFilteredBanks = () => {
    if (!Array.isArray(bankData)) {
      return [];
    }
    return bankData.filter((bank) => {
      const matchesSearch =
        bank.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bank.contactPersonName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bank.contactNumber?.includes(searchQuery);
      const matchesStatus = statusFilter === "all" || bank.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Active":
        return { backgroundColor: "#e8f5e9", color: "#2e7d32" };
      case "Inactive":
        return { backgroundColor: "#ffebee", color: "#d32f2f" };
      default:
        return { backgroundColor: "#f5f5f5", color: "#666" };
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newBank.name.trim()) newErrors.name = "Bank name is required";
    if (!newBank.contactPersonName.trim()) newErrors.contactPersonName = "Contact person name is required";
    if (!newBank.contactNumber.trim()) newErrors.contactNumber = "Contact number is required";
    if (newBank.contactNumber && !/^\d{10}$/.test(newBank.contactNumber.replace(/\D/g, ''))) {
      newErrors.contactNumber = "Contact number must be 10 digits";
    }
    return newErrors;
  };

  const handleEdit = (bank) => {
    setEditingBank(bank);
    setNewBank({
      name: bank.name || "",
      contactPersonName: bank.contactPersonName || "",
      contactNumber: bank.contactNumber || "",
      status: bank.rawStatus === true || bank.status === "Active" ? "Active" : "Inactive",
    });
    setOpenModal(true);
  };

  const handleDeleteClick = (bank) => {
    setSelectedBankForDelete(bank);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedBankForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${baseURL}/api/globalMasterDetails/${selectedBankForDelete.id}`, {
        method: "PUT",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDeleted: true }),
      });

      if (!response.ok) throw new Error("Delete failed");

      setAlertConfig({ title: 'Success', message: 'Bank deleted successfully!' });
      setAlertOpen(true);
      fetchBanks(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete bank.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedBankForDelete(null);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleReset = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setPage(0);
    setErrors({});
    setSuccessMessage("");
  };

  const handleOpenModal = () => {
    setEditingBank(null);
    setNewBank({
      name: "",
      contactPersonName: "",
      contactNumber: "",
      status: "Active",
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingBank(null);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewBank({ ...newBank, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleAddBank = async () => {
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsLoadingModal(true);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const payload = {
        type: "bank",
        details: {
          bankName: newBank.name.trim(),
          contactPersonName: newBank.contactPersonName.trim(),
          contactNumber: newBank.contactNumber.trim(),
        },
        status: newBank.status === "Active",
      };

      const url = editingBank
        ? `${baseURL}/api/globalMasterDetails/${editingBank.id}`
        : `${baseURL}/api/globalMasterDetails`;
      const method = editingBank ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `Failed to ${editingBank ? "update" : "add"} bank: ${response.statusText}`
        );
      }

      setAlertConfig({
        title: 'Success',
        message: editingBank ? "Bank updated successfully!" : "Bank added successfully!"
      });
      setAlertOpen(true);
      await fetchBanks(true);
      handleCloseModal();
    } catch (error) {
      setErrors({ general: error.message || "An error occurred. Please try again." });
    } finally {
      setIsLoadingModal(false);
    }
  };

  const filteredBanks = getFilteredBanks();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <BankIcon sx={{ mr: 1, color: "primary.main", fontSize: 28 }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            Bank Master Management
          </Typography>
        </Box>
        {/* <Box sx={{ display: "flex", gap: 1 }}> */}
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchBanks()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, flex: { xs: 1, sm: 'none' } }}
            >
              Add New Bank
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal Alerts & Confirms */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the bank <strong>{selectedBankForDelete?.name}</strong>? This action cannot be undone.
      </ConfirmModal>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Banks..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Filter by Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              SelectProps={{
                native: true,
              }}
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={5}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                setPage(0);
                const count = getFilteredBanks().length;
                setAlertConfig({
                  title: 'Search Results',
                  message: count > 0 ? `Found ${count} banks matching your criteria.` : 'No banks found matching your criteria.'
                });
                setAlertOpen(true);
              }}
              startIcon={<SearchIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, height: '56px', borderRadius: 2 }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading Indicator */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table Section */}
      {!isLoading && (
        <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Bank Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Contact Person Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Contact Number</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBanks.length > 0 ? (
                  filteredBanks
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((bank, index) => (
                      <TableRow key={bank.id} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">{bank.name || "N/A"}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">{bank.contactPersonName || "N/A"}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">{bank.contactNumber || "N/A"}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={bank.status || "N/A"}
                            size="small"
                            color={bank.status === "Active" ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            {canWrite && (
                              <Tooltip title="Edit Bank">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleEdit(bank)}
                                  disabled={isLoading}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canWrite && (
                              <Tooltip title="Delete Bank">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleDeleteClick(bank)}
                                  disabled={isLoading}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        No banks found matching your criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredBanks.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Add/Edit Bank Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            fontWeight: "bold",
          }}
        >
          {editingBank ? "Edit Bank" : "Add New Bank"}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bank Name"
                name="name"
                value={newBank.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name || "e.g., State Bank of India, HDFC Bank, etc."}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Person Name"
                name="contactPersonName"
                value={newBank.contactPersonName}
                onChange={handleInputChange}
                error={!!errors.contactPersonName}
                helperText={errors.contactPersonName}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Number"
                name="contactNumber"
                value={newBank.contactNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  handleInputChange({ target: { name: 'contactNumber', value } });
                }}
                error={!!errors.contactNumber}
                helperText={errors.contactNumber || "10 digits"}
                margin="normal"
                inputProps={{ maxLength: 10 }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Status"
                name="status"
                value={newBank.status}
                onChange={handleInputChange}
                margin="normal"
                SelectProps={{
                  native: true,
                }}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </TextField>
            </Grid>
          </Grid>
          {errors.general && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.general}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} disabled={isLoadingModal}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddBank}
            disabled={isLoadingModal}
            startIcon={isLoadingModal ? <CircularProgress size={20} /> : null}
          >
            {isLoadingModal ? "Saving..." : editingBank ? "Update Bank" : "Add Bank"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Bank;